import { Text } from '@innovationdepartment/proxima-ui';
import { AddRounded } from '@mui/icons-material';
import DestinationsTable from '../../components/DestinationsTable';
import { DefinitionPanel, PanelSectionHeading } from '../../components/form.styles';
import { EmptyState, PrimaryButton } from '../../components/shared.styles';
import { DELIVERY_BLOCKED_REASONS } from '../../lib/definitionDelivery';
import { groupByPlatform, platformLabel } from '../../lib/deliverySummary';
import {
  destinationDeliveryOutcome,
  isLiveDestination,
  latestDeliveryByDestination
} from '../../lib/deriveAudienceStatus';
import type { TosCheck } from '../../store/destinationsSlice';
import type { TargetSelection } from '../../store/wizardTypes';
import type {
  AdAccountsResponse,
  AdPlatform,
  AudienceDelivery,
  AudienceDestination,
  AudienceStatus,
  LookalikeRatio,
  ValidationError,
  ValueMetric
} from '../../types';
import { DestinationsGroupTitle, SectionTitle } from './DefinitionDetails.styles';
import DeliveryPanel from './DeliveryPanel';

export type DeliveriesTabProps = {
  destinations: AudienceDestination[];
  /** The latest run's delivery attempts; each destination's latest one shows beside its status. */
  deliveries: AudienceDelivery[];
  /** Why the destinations could not be loaded; shown instead of the empty state. */
  destinationsError: string | null;
  adAccounts: AdAccountsResponse | null;
  adAccountsError: string | null;
  tosCheckByAdAccountId?: Record<string, TosCheck>;
  onRecheckTos?: (adAccountId: string) => void;
  definitionStatus: AudienceStatus;
  /** Whether the Deliver-to action renders; the destinations list renders either way. */
  canDeliver: boolean;
  deliverOpen: boolean;
  targets: TargetSelection;
  deliveryErrors: ValidationError[];
  onOpenDeliver: () => void;
  onSelectPlatform: (platform: AdPlatform) => void;
  onToggleAdAccount: (adAccountId: string) => void;
  onToggleRatio: (ratio: LookalikeRatio) => void;
  onSelectValueMetric: (valueMetric: ValueMetric | null) => void;
};

/**
 * Destinations grouped by platform, with the Deliver-to panel when `canDeliver` is set.
 *
 * The panel's Deliver and Cancel live in the page header, so this tab renders the form only.
 */
const DeliveriesTab = (props: DeliveriesTabProps) => {
  const { destinations } = props;
  const latestDeliveries = latestDeliveryByDestination(props.deliveries);
  const deleted = props.definitionStatus === 'deleted';
  const panelOpen = props.canDeliver && props.deliverOpen;
  const deliveredAccountIds = new Set(
    destinations
      .filter(row => isLiveDestination(row) && row.kind === 'seed')
      .map(row => row.adAccountId)
  );
  const deliverButton = props.canDeliver ? (
    <PrimaryButton
      label="Deliver to"
      size="small"
      startIcon={<AddRounded />}
      disabled={deleted || props.deliverOpen}
      onClick={props.onOpenDeliver}
    />
  ) : null;

  return (
    <>
      {panelOpen ? (
        <DeliveryPanel
          definitionStatus={props.definitionStatus}
          targets={props.targets}
          adAccounts={props.adAccounts}
          adAccountsError={props.adAccountsError}
          tosCheckByAdAccountId={props.tosCheckByAdAccountId}
          onRecheckTos={props.onRecheckTos}
          deliveredAccountIds={deliveredAccountIds}
          errors={props.deliveryErrors}
          onSelectPlatform={props.onSelectPlatform}
          onToggleAdAccount={props.onToggleAdAccount}
          onToggleRatio={props.onToggleRatio}
          onSelectValueMetric={props.onSelectValueMetric}
        />
      ) : null}
      {props.destinationsError ? (
        <div role="alert">
          <Text variant="body2" color="red60">
            Couldn't load this audience's destinations: {props.destinationsError}
          </Text>
        </div>
      ) : null}
      {destinations.length === 0 && !panelOpen && !props.destinationsError ? (
        <EmptyState>
          <Text variant="body1Semibold">Not delivered yet</Text>
          <Text variant="body2" color="neutral60">
            {props.canDeliver
              ? 'Deliver this audience to ad accounts to start building platform audiences.'
              : 'This audience has no destinations.'}
          </Text>
          {deliverButton}
        </EmptyState>
      ) : null}
      {destinations.length > 0 ? (
        <>
          <SectionTitle>
            <PanelSectionHeading>Destinations</PanelSectionHeading>
            {deliverButton}
          </SectionTitle>
          {groupByPlatform(destinations).map(group => {
            const live = group.destinations.filter(isLiveDestination);
            const failedCount = live.filter(
              row =>
                destinationDeliveryOutcome(row, latestDeliveries.get(row.id))?.kind === 'failed'
            ).length;
            return (
              /* Each platform's destinations sit in the create page's panel frame */
              <DefinitionPanel
                key={group.platform}
                aria-labelledby={`destinations-${group.platform}-heading`}
              >
                <DestinationsGroupTitle id={`destinations-${group.platform}-heading`}>
                  {platformLabel(group.platform)} · {live.length} live
                  {failedCount > 0 ? ` · ${failedCount} failed` : ''}
                </DestinationsGroupTitle>
                <DestinationsTable
                  destinations={group.destinations}
                  latestDeliveries={latestDeliveries}
                  adAccounts={props.adAccounts}
                />
              </DefinitionPanel>
            );
          })}
        </>
      ) : null}
      {deleted ? (
        <Text variant="body3" color="neutral60">
          {DELIVERY_BLOCKED_REASONS.deleted}
        </Text>
      ) : null}
    </>
  );
};

export default DeliveriesTab;
